import * as RadixToast from "@radix-ui/react-toast";
import clsx from "clsx";
import { useState, useCallback, useEffect } from "react";

// ── Types ──────────────────────────────────────────────────────────────────

export type ToastVariant = "success" | "error" | "info";

interface ToastItem {
  id:           string;
  variant:      ToastVariant;
  title:        string;
  description?: string;
}

// ── Internal pub/sub bus ───────────────────────────────────────────────────

type Listener = (item: ToastItem) => void;
const _listeners = new Set<Listener>();
let   _counter   = 0;

function _emit(item: ToastItem) {
  _listeners.forEach((l) => l(item));
}

function _addListener(fn: Listener): () => void {
  _listeners.add(fn);
  return () => void _listeners.delete(fn);
}

// ── Public imperative API ─────────────────────────────────────────────────

function _create(variant: ToastVariant, title: string, description?: string) {
  const item: ToastItem = { id: String(++_counter), variant, title };
  if (description !== undefined) item.description = description;
  _emit(item);
}

/**
 * Imperative toast helper. Call from anywhere — no component needed at the call site.
 *
 * ```ts
 * toast.success("Сохранено");
 * toast.error("Ошибка сервера", "Попробуйте позже");
 * toast("Информация");
 * ```
 *
 * Requires `<ToastViewport />` to be mounted once in the app tree.
 */
export const toast = Object.assign(
  (title: string, description?: string) => _create("info", title, description),
  {
    success: (title: string, description?: string) => _create("success", title, description),
    error:   (title: string, description?: string) => _create("error",   title, description),
    info:    (title: string, description?: string) => _create("info",    title, description),
  },
);

// ── Internal ToastItem renderer ───────────────────────────────────────────

const accentBar: Record<ToastVariant, string> = {
  success: "border-l-2 border-success",
  error:   "border-l-2 border-danger",
  info:    "border-l-2 border-accent",
};

function ToastRow({
  item,
  onRemove,
}: {
  item:     ToastItem;
  onRemove: (id: string) => void;
}) {
  return (
    <RadixToast.Root
      open
      duration={4000}
      onOpenChange={(open) => {
        if (!open) onRemove(item.id);
      }}
      className={clsx(
        "group relative flex flex-col gap-0.5 rounded-md bg-surface px-3 py-2.5",
        "shadow-card",
        accentBar[item.variant],
      )}
    >
      <RadixToast.Title className="text-sm font-medium text-fg">
        {item.title}
      </RadixToast.Title>
      {item.description && (
        <RadixToast.Description className="text-xs text-muted">
          {item.description}
        </RadixToast.Description>
      )}
      <RadixToast.Close asChild>
        <button
          aria-label="Закрыть"
          className={clsx(
            "absolute right-2 top-2 rounded p-0.5 text-muted transition-colors",
            "opacity-0 group-hover:opacity-100 hover:text-fg",
          )}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
            <path
              d="M1 1l10 10M11 1L1 11"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </RadixToast.Close>
    </RadixToast.Root>
  );
}

// ── Viewport (mount once in app root) ────────────────────────────────────

/**
 * Mount once at the app root (e.g. in `main.tsx`).
 * Receives toasts from `toast()` / `toast.success()` etc.
 */
export function ToastViewport() {
  const [items, setItems] = useState<ToastItem[]>([]);

  useEffect(() => _addListener((item) => setItems((p) => [...p, item])), []);

  const remove = useCallback(
    (id: string) => setItems((p) => p.filter((i) => i.id !== id)),
    [],
  );

  return (
    <RadixToast.Provider swipeDirection="right">
      {items.map((item) => (
        <ToastRow key={item.id} item={item} onRemove={remove} />
      ))}
      <RadixToast.Viewport className="fixed bottom-4 right-4 z-50 flex w-80 flex-col gap-2 outline-none" />
    </RadixToast.Provider>
  );
}
